import { FileSpreadsheet } from 'lucide-react';
import { useMemo } from 'react';
import {
  applyModifications,
  buildGalleryDoc,
  createEmptyDoc,
  createQrLayer,
  createShapeLayer,
  createTextLayer,
  findGalleryEntry,
  resizeDoc,
  type LocaleId,
  type TemplateDoc,
} from '@bannermatic/core';
import { DocPreview, useBuiltinFonts } from '../components/DocPreview';
import type { Visual as VisualDef } from './content/shared';

const CERTIFICATE: Record<LocaleId, string> = {
  en: 'CERTIFICATE',
  uk: 'СЕРТИФІКАТ',
  pl: 'CERTYFIKAT',
  ro: 'CERTIFICAT',
  es: 'CERTIFICADO',
  ru: 'СЕРТИФИКАТ',
};

/** A certificate on A4 landscape (the gallery has none): name and course come from the rows. */
function certificateDoc(locale: LocaleId): TemplateDoc {
  const doc = createEmptyDoc(1754, 1240);
  doc.background = { type: 'solid', color: '#FFFBEB' };
  doc.layers.push(
    createShapeLayer({
      name: 'frame',
      x: 60,
      y: 60,
      width: 1634,
      height: 1120,
      borderRadius: 24,
      fill: { type: 'solid', color: '#FFFFFF' },
      strokeColor: '#D97706',
      strokeWidth: 10,
    }),
    createTextLayer({
      name: 'heading',
      x: 160,
      y: 190,
      width: 1434,
      height: 120,
      text: CERTIFICATE[locale],
      fontFamily: 'Playfair Display',
      fontWeight: 800,
      fontSize: 110,
      letterSpacing: 12,
      align: 'center',
      color: '#92400E',
    }),
    createTextLayer({
      name: 'name',
      x: 160,
      y: 450,
      width: 1434,
      height: 160,
      text: 'Anna Koval',
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: 120,
      align: 'center',
      verticalAlign: 'middle',
      color: '#1F2937',
      autoFit: true,
      minFontSize: 60,
    }),
    createShapeLayer({
      name: 'rule',
      x: 577,
      y: 640,
      width: 600,
      height: 6,
      fill: { type: 'solid', color: '#F59E0B' },
    }),
    createTextLayer({
      name: 'course',
      x: 260,
      y: 700,
      width: 1234,
      height: 140,
      text: 'Data analytics from scratch',
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: 58,
      lineHeight: 1.2,
      align: 'center',
      color: '#4B5563',
      maxLines: 2,
    }),
    createQrLayer({
      name: 'qr',
      x: 160,
      y: 930,
      width: 170,
      height: 170,
      value: 'https://bannermatic.app/verify/2026-0142',
    }),
    createTextLayer({
      name: 'number',
      x: 1100,
      y: 1010,
      width: 494,
      height: 80,
      text: '№ 2026-0142',
      fontFamily: 'JetBrains Mono',
      fontSize: 40,
      align: 'right',
      verticalAlign: 'middle',
      color: '#9CA3AF',
    }),
  );
  return doc;
}

function baseDoc(template: string, locale: LocaleId): TemplateDoc | null {
  if (template === 'certificate') return certificateDoc(locale);
  const entry = findGalleryEntry(template);
  return entry ? buildGalleryDoc(entry, locale) : null;
}

/** Size in px of the box each preview is fitted into. */
const BOX = 260;

/**
 * The page's picture: one template turned into several banners — from the data rows of the
 * page (a small table next to the results) or re-laid out for several sizes.
 */
export function Visual({
  visual,
  samples,
  locale,
  caption,
}: {
  visual: VisualDef;
  samples?: string[][];
  locale: LocaleId;
  caption: string;
}) {
  useBuiltinFonts();
  const docs = useMemo(() => {
    const base = baseDoc(visual.template, locale);
    if (!base) return [];
    if (visual.kind === 'sizes') {
      return visual.sizes.map(([w, h]) =>
        w === base.width && h === base.height ? base : resizeDoc(base, w, h),
      );
    }
    return (samples ?? []).map(
      (row) =>
        applyModifications(
          base,
          Object.fromEntries(visual.fields.map((field, i) => [field, row[i] ?? ''])),
        ).doc,
    );
  }, [visual, samples, locale]);

  return (
    <figure className="overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-5 ring-1 ring-indigo-100 sm:p-8">
      {visual.kind === 'rows' && samples?.length ? (
        <div className="mb-6 overflow-x-auto rounded-xl bg-white ring-1 ring-zinc-200">
          <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-2 text-xs text-zinc-500">
            <FileSpreadsheet className="size-4 text-emerald-600" /> data.csv
          </div>
          <table className="w-full text-left text-sm">
            <thead className="font-mono text-xs text-zinc-500">
              <tr>
                {visual.fields.map((f) => (
                  <th key={f} className="px-4 py-2 font-medium">
                    {f}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-zinc-800">
              {samples.map((row, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  {visual.fields.map((f, j) => (
                    <td key={f} className="px-4 py-2">
                      {row[j]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="flex flex-wrap items-end justify-center gap-5">
        {docs.map((doc, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <div className="overflow-hidden rounded-lg shadow-lg shadow-indigo-900/10 ring-1 ring-black/5">
              <DocPreview doc={doc} box={BOX} />
            </div>
            {visual.kind === 'sizes' && (
              <span className="font-mono text-xs text-zinc-500">
                {doc.width} × {doc.height}
              </span>
            )}
          </div>
        ))}
      </div>
      <figcaption className="mt-5 text-center text-sm text-zinc-500">{caption}</figcaption>
    </figure>
  );
}
